// src/views/consumo/ConsumoList.jsx
import React, { useState, useEffect, useCallback } from "react";
import consumoService      from "../../services/consumoService";
import condominiosService  from "../../services/condominiosService";
import unidadesService     from "../../services/unidadesService";
import contasContabeisService from "../../services/contasContabeisService";
import contasBancariasService from "../../services/contasBancariasService";
import fornecedoresService from "../../services/fornecedoresService";
import { useAuth }         from "../../contexts/AuthContext";
import FotoLeituraButton   from "./FotoLeituraButton";
import Banner from "../../components/ui/Banner";
import ConfirmModal from "../../components/ui/ConfirmModal";

const fmtM  = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtN3 = v => Number(v||0).toLocaleString("pt-BR",{minimumFractionDigits:3,maximumFractionDigits:3});
const fmtD  = d => { try{ const s=(d||"").split("T")[0].split(" ")[0]; return s?new Date(s+"T12:00:00").toLocaleDateString("pt-BR"):"—"; }catch{ return d||"—"; } };

const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const mesLabel = m => { if(!m) return "—"; const [a,mm]=m.split("-"); return `${MESES[parseInt(mm)-1]}/${a.slice(2)}`; };

const hoje = () => new Date().toISOString().split("T")[0];
const mesAtual = () => new Date().toISOString().slice(0,7);

// ── Config padrão ──────────────────────────────────────────────────────────
const CFG_EMPTY = {
  tpConsumo:"Agua", ativo:1,
  idCtReceita:"", idCtDespesa:"", idContaBancaria:"", idFornecedor:"",
  franquiaM3:"0", tpDiferenca:"Absorver", idCtDiferenca:"",
  descDiferenca:"Uso Comum", alertaPercVariacao:"30",
  fatorConversao:"1", unidadeMedida:"m3",
};

export default function ConsumoList() {
  const { condominioAtivo } = useAuth();
  const { podeEditar } = useAuth();
  const canEdit = podeEditar("consumo");
  const [idCondo, setIdCondo]   = useState("");
  const [condominios, setCondominios] = useState([]);
  const [aba, setAba]           = useState("leituras");  // leituras | medidores | config | relatorio
  const [tpConsumo, setTpConsumo] = useState("Agua");

  // Config
  const [cfg, setCfg]           = useState(null);
  const [formCfg, setFormCfg]   = useState(CFG_EMPTY);
  const [savingCfg, setSavingCfg] = useState(false);

  // Medidores
  const [medidores, setMedidores] = useState([]);
  const [unidades, setUnidades]   = useState([]);
  const [showMedModal, setShowMedModal] = useState(false);
  const [editMed, setEditMed]     = useState(null);
  const [formMed, setFormMed]     = useState({idUnidade:"",nrMedidor:"",leituraInicial:"0",dtInstalacao:"",observacao:""});

  // Leituras
  const [ciclos, setCiclos]       = useState([]);
  const [cicloAtivo, setCicloAtivo] = useState(null);
  const [cicloLeituras, setCicloLeituras] = useState({});
  const [leituras, setLeituras]   = useState([]);
  const [preview, setPreview]     = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [formCiclo, setFormCiclo] = useState({
    competencia: mesAtual(), dtLeitura: hoje(),
    dtVencimento: "", vlrContaConcessionaria:"", consumoTotalM3:"",
  });

  // Auxiliares
  const [contasContabeis, setContasContabeis] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [fornecedores, setFornecedores]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);

  // Relatório
  const [relatorio, setRelatorio] = useState(null);
  const [loadingRel, setLoadingRel] = useState(false);
  const [relCompetencia, setRelCompetencia] = useState(mesAtual());

  // UX: banner + modal de confirmação
  const [banner, setBanner] = useState(null);
  const [confirmacao, setConfirmacao] = useState(null);
  const notify = useCallback((tipo, msg) => {
    setBanner({ tipo, msg });
    if (tipo === "success" || tipo === "info") {
      setTimeout(() => setBanner((b) => (b && b.msg === msg ? null : b)), 4000);
    }
  }, []);
  const askConfirm = useCallback(
    (cfg) => new Promise((resolve) => {
      setConfirmacao({
        titulo: cfg.titulo || "Confirmar",
        mensagem: cfg.mensagem,
        perigo: !!cfg.perigo,
        textoOk: cfg.textoOk || "Confirmar",
        onConfirm: () => { setConfirmacao(null); resolve(true); },
        onCancel:  () => { setConfirmacao(null); resolve(false); },
      });
    }),
    []
  );

  useEffect(() => {
    condominiosService.listar({limit:100}).then(r=>setCondominios(r.data||[])).catch(()=>{});
  }, []);

  // Sync with navbar condominioAtivo — updates whenever user changes it
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  // Load fornecedores
  useEffect(() => {
    if (!idCondo) return;
    // Load fornecedores filtered by condominio, fallback to global list
    const loadFornecedores = async () => {
      try {
        let r = await fornecedoresService.listar({idCondominio:idCondo, limit:200});
        if ((r.data||[]).length > 0) { setFornecedores(r.data); return; }
        // Fallback: show all fornecedores regardless of condominio
        r = await fornecedoresService.listar({limit:200});
        setFornecedores(r.data||[]);
      } catch(e) {
        // Fallback on error: try without filter
        try {
          const r = await fornecedoresService.listar({limit:200});
          setFornecedores(r.data||[]);
        } catch {}
      }
    };
    loadFornecedores();
  }, [idCondo]);

  useEffect(() => {
    if (!idCondo) return;
    contasContabeisService.listar({idCondominio:idCondo,natureza:"Analitica",limit:200}).then(r=>setContasContabeis(r.data||[])).catch(()=>{});
    contasBancariasService.listar({idCondominio:idCondo}).then(r=>setContasBancarias(r.data||[])).catch(()=>{});
    unidadesService.listar({idCondominio:idCondo,limit:500}).then(r=>setUnidades(r.data||[])).catch(()=>{});
    carregarConfig();
    carregarMedidores();
    carregarCiclos();
  }, [idCondo, tpConsumo]);

  const carregarConfig = useCallback(async () => {
    if (!idCondo) return;
    try {
      const r = await consumoService.buscarConfig(idCondo, tpConsumo);
      if (r.data) { setCfg(r.data); setFormCfg({...CFG_EMPTY,...r.data}); }
      else { setCfg(null); setFormCfg({...CFG_EMPTY, tpConsumo}); }
    } catch { setCfg(null); setFormCfg({...CFG_EMPTY, tpConsumo}); }
  }, [idCondo, tpConsumo]);

  const carregarMedidores = useCallback(async () => {
    if (!idCondo) return;
    try {
      const r = await consumoService.listarMedidores({idCondominio:idCondo, tpConsumo});
      setMedidores(r.data||[]);
    } catch(e) { console.error("Erro medidores:", e); }
  }, [idCondo, tpConsumo]);

  const carregarCiclos = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      const r = await consumoService.listarCiclos({idCondominio:idCondo, tpConsumo});
      setCiclos(r.data||[]);
    } catch(e) { console.error("Erro ciclos:", e); }
    setLoading(false);
  }, [idCondo, tpConsumo]);

  const handleSalvarConfig = async () => {
    setSavingCfg(true);
    try {
      await consumoService.salvarConfig({...formCfg, idCondominio:idCondo, tpConsumo});
      await carregarConfig();
      notify("success", "Configuração salva com sucesso!");
    } catch(e) { notify("error", e.message); }
    setSavingCfg(false);
  };

  const handleSalvarMedidor = async () => {
    if (!formMed.idUnidade) return notify("warning", "Selecione a unidade.");
    setSaving(true);
    try {
      if (editMed?.id) await consumoService.atualizarMedidor(editMed.id, {...formMed, tpConsumo, idCondominio:idCondo});
      else             await consumoService.criarMedidor({...formMed, tpConsumo, idCondominio:idCondo});
      setShowMedModal(false);
      setEditMed(null);
      setFormMed({nrMedidor:"",idUnidade:"",leituraInicial:0,dtInstalacao:"",ativo:true,obs:""});
      await carregarMedidores();
      notify("success", editMed?.id ? "Medidor atualizado." : "Medidor cadastrado.");
    } catch(e) { notify("error", e.message); }
    setSaving(false);
  };

  const handlePreview = async () => {
    if (!formCiclo.vlrContaConcessionaria || !formCiclo.consumoTotalM3) {
      return notify("warning", "Informe o valor da conta e o consumo total em m³.");
    }
    setLoadingPreview(true);
    setPreview(null);
    try {
      const r = await consumoService.previewLeituras({
        idCondominio: idCondo, tpConsumo,
        ...formCiclo,
        leituras: leituras.map(l => {
          // Pega o vlrTotal calculado no preview para garantir consistência
          const previewItem = (preview?.itens||[]).find(it=>it.idMedidor===l.idMedidor);
          return {
            idMedidor: l.idMedidor,
            leituraAtual: l.leituraAtual,
            vlrTotal: previewItem?.vlrTotal ?? null,
            consumoM3: previewItem?.consumoM3 ?? null,
          };
        }),
      });
      setPreview(r.data);
    } catch(e) { notify("error", e.message); }
    setLoadingPreview(false);
  };

  const handleConfirmar = async () => {
    if (!preview) return;
    // Confirma se há leituras menores que a anterior (medidor zerou?)
    const zeradas = leituras.filter(l => parseFloat(l.leituraAtual) < parseFloat(l.leituraAnterior));
    if (zeradas.length > 0) {
      const detalhe = zeradas.map(l => `• ${l.nmUnidade}: ${l.leituraAnterior} → ${l.leituraAtual}`).slice(0,5).join(" | ");
      const sufixo = zeradas.length > 5 ? ` ... e mais ${zeradas.length-5}` : "";
      const ok = await askConfirm({
        titulo: "Leituras menores que a anterior",
        mensagem: `${zeradas.length} medidor(es) com leitura MENOR que a anterior: ${detalhe}${sufixo}. Isso normalmente indica troca de medidor. Continuar mesmo assim?`,
        perigo: true, textoOk: "Continuar",
      });
      if (!ok) return;
    }
    // Confirma alertas de variação alta
    const alertas = (preview.itens||[]).filter(it => it.alerta);
    if (alertas.length > 0) {
      const ok = await askConfirm({
        titulo: "Variação de consumo alta",
        mensagem: `Existem ${alertas.length} unidade(s) com variação acima de ${cfg?.alertaPercVariacao || 30}%. Reveja os números antes de confirmar. Deseja continuar?`,
        textoOk: "Continuar",
      });
      if (!ok) return;
    }
    const ok = await askConfirm({
      titulo: "Confirmar lançamentos",
      mensagem: "Confirmar lançamentos? Serão criadas Contas a Receber para cada unidade.",
      textoOk: "Confirmar lançamentos",
    });
    if (!ok) return;
    setConfirmando(true);
    try {
      await consumoService.confirmarLeituras({
        idCondominio: idCondo, tpConsumo,
        ...formCiclo,
        leituras: leituras.map(l => {
          const previewItem = (preview?.itens||[]).find(it=>it.idMedidor===l.idMedidor);
          return {
            idMedidor: l.idMedidor,
            leituraAtual: l.leituraAtual,
            vlrTotal: previewItem?.vlrTotal ?? null,
            consumoM3: previewItem?.consumoM3 ?? null,
          };
        }),
      });

      // Sobe fotos pendentes (em background — não bloqueia o sucesso da confirmação)
      const comFoto = leituras.filter(l => l.fotoPendente?.base64);
      if (comFoto.length > 0) {
        Promise.allSettled(comFoto.map(l =>
          consumoService.uploadFoto({
            idMedidor:   l.idMedidor,
            competencia: formCiclo.competencia,
            base64:      l.fotoPendente.base64,
            mimeType:    l.fotoPendente.mimeType,
            leituraDigitada: l.leituraAtual,
            runOcr:      false, // já rodamos no preview
          }).catch(()=>null)
        )).then((r) => {
          const ok = r.filter(x => x.status==="fulfilled").length;
          if (ok > 0) console.log(`[consumo] ${ok}/${comFoto.length} foto(s) salva(s).`);
        });
      }

      setPreview(null);
      setLeituras([]);
      await carregarCiclos();
      notify("success", `Leituras confirmadas! Contas a Receber geradas para cada unidade.${comFoto.length>0?` ${comFoto.length} foto(s) sendo enviada(s) em segundo plano.`:""}`);
    } catch(e) { notify("error", e.message); }
    setConfirmando(false);
  };

  const handleCarregarLeituras = () => {
    const ls = medidores.filter(m=>m.ativo).map(m => ({
      idMedidor:       m.id,
      idUnidade:       m.idUnidade,
      nmUnidade:       m.nmUnidade || unidades.find(u=>String(u.id)===String(m.idUnidade))?.unidade || "—",
      bloco:           m.bloco,
      nrMedidor:       m.nrMedidor,
      leituraAnterior: m.ultimaLeitura != null ? m.ultimaLeitura : (m.leituraInicial || 0),
      mediaConsumo:    m.mediaConsumo != null ? parseFloat(m.mediaConsumo) : 0,
      leituraAtual:    "",
    }));
    setLeituras(ls);
  };

  // ── Import CSV (leitor offline com tablet) ──
  const handleImportCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      // Espera CSV: idMedidor;leituraAtual  ou  nrMedidor;leituraAtual
      const lines = text.split(/\r?\n/).filter(Boolean);
      const sep = lines[0].includes(";") ? ";" : ",";
      // Detecta header
      let start = 0;
      const header = lines[0].toLowerCase();
      if (header.includes("medidor") || header.includes("leitura")) start = 1;

      const map = {};
      for (let i = start; i < lines.length; i++) {
        const cols = lines[i].split(sep).map(c => c.trim().replace(/^"|"$/g,""));
        if (cols.length < 2) continue;
        const key = cols[0];
        const val = parseFloat(String(cols[1]).replace(",", "."));
        if (!isNaN(val)) map[key] = val;
      }

      const ls = (leituras.length > 0 ? leituras : medidores.filter(m=>m.ativo).map(m=>({
        idMedidor:       m.id,
        idUnidade:       m.idUnidade,
        nmUnidade:       m.nmUnidade,
        bloco:           m.bloco,
        nrMedidor:       m.nrMedidor,
        leituraAnterior: m.ultimaLeitura != null ? m.ultimaLeitura : (m.leituraInicial || 0),
        mediaConsumo:    m.mediaConsumo != null ? parseFloat(m.mediaConsumo) : 0,
        leituraAtual:    "",
      })));

      let preencheu = 0;
      const nl = ls.map(l => {
        const byId = map[String(l.idMedidor)];
        const byNr = l.nrMedidor && map[String(l.nrMedidor)];
        const val  = byId !== undefined ? byId : byNr;
        if (val !== undefined) { preencheu++; return { ...l, leituraAtual: String(val) }; }
        return l;
      });
      setLeituras(nl);
      notify("success", `Importação concluída: ${preencheu} leitura(s) preenchida(s) de ${nl.length} medidor(es).`);
    };
    reader.readAsText(file, "UTF-8");
  };

  const handleCarregarRelatorio = async () => {
    setLoadingRel(true);
    try {
      const r = await consumoService.relatorio({idCondominio:idCondo, tpConsumo, competencia:relCompetencia||undefined});
      setRelatorio(r.data || r);
    } catch(e) { notify("error", e.message); }
    setLoadingRel(false);
  };

  const icone = tpConsumo === "Agua" ? "bi-droplet-fill" : "bi-fire";
  const corTp = tpConsumo === "Agua" ? "#1D4ED8" : "#C2410C";
  const bgTp  = tpConsumo === "Agua" ? "#EFF6FF"  : "#FFF7ED";

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header" style={{marginBottom:20}}>
        <div>
          <h1 className="page-title">
            <i className={`bi ${icone} me-2`} style={{color:corTp,fontSize:22}}/>
            Água e Gás
          </h1>
          <p className="page-subtitle">Leituras de consumo, rateio e lançamentos contábeis</p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {/* Seletor Água / Gás */}
          <div style={{display:"flex",background:"var(--gray-100)",borderRadius:8,padding:3,gap:3}}>
            {["Agua","Gas"].map(t => (
              <button key={t} onClick={()=>setTpConsumo(t)}
                style={{padding:"6px 16px",borderRadius:6,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,
                  background:tpConsumo===t?"#fff":"transparent",
                  color:tpConsumo===t?(t==="Agua"?"#1D4ED8":"#C2410C"):"var(--gray-500)",
                  boxShadow:tpConsumo===t?"0 1px 3px rgba(0,0,0,.1)":"none"}}>
                <i className={`bi ${t==="Agua"?"bi-droplet-fill":"bi-fire"} me-1`}/>
                {t==="Agua"?"Água":"Gás"}
              </button>
            ))}
          </div>

        </div>
      </div>

      {!idCondo ? (
        <div style={{textAlign:"center",padding:"80px 0",color:"var(--gray-400)"}}>
          <i className="bi bi-buildings" style={{fontSize:48,display:"block",marginBottom:12}}/>
          <p style={{fontSize:16}}>Selecione um condomínio</p>
        </div>
      ) : (
        <>
          {/* ── Abas ── */}
          <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:"1px solid var(--gray-200)",paddingBottom:0}}>
            {[
              {id:"leituras",  icon:"bi-pencil-square",   label:"Lançar Leituras"},
              {id:"historico", icon:"bi-clock-history",   label:"Histórico"},
              {id:"medidores", icon:"bi-speedometer",     label:"Medidores"},
              {id:"config",    icon:"bi-gear-fill",       label:"Configuração"},
              {id:"relatorio", icon:"bi-bar-chart-line",  label:"Relatório"},
            ].map(t => (
              <button key={t.id} onClick={()=>setAba(t.id)}
                style={{padding:"8px 16px",border:"none",background:"transparent",cursor:"pointer",
                  fontSize:13,fontWeight:aba===t.id?700:400,
                  color:aba===t.id?corTp:"var(--gray-500)",
                  borderBottom:aba===t.id?`2px solid ${corTp}`:"2px solid transparent",
                  marginBottom:-1}}>
                <i className={`bi ${t.icon} me-1`}/>{t.label}
              </button>
            ))}
          </div>

          {/* ══ ABA: LANÇAR LEITURAS ══ */}
          {aba==="leituras" && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {/* Painel esquerdo: dados do ciclo */}
              <div style={{background:"var(--bg-surface)",borderRadius:12,padding:20,border:"1px solid var(--gray-200)"}}>
                <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)",marginBottom:14,
                  display:"flex",alignItems:"center",gap:8}}>
                  <i className={`bi bi-receipt`} style={{color:corTp}}/>
                  Conta da Concessionária
                </div>
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label">Competência *</label>
                    <input type="month" className="form-control"
                      value={formCiclo.competencia}
                      onChange={e=>setFormCiclo(p=>({...p,competencia:e.target.value}))}/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Data da Leitura</label>
                    <input type="date" className="form-control"
                      value={formCiclo.dtLeitura}
                      onChange={e=>setFormCiclo(p=>({...p,dtLeitura:e.target.value}))}/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Valor da Conta (R$) *</label>
                    <input type="number" step="0.01" className="form-control"
                      placeholder="0,00"
                      value={formCiclo.vlrContaConcessionaria}
                      onChange={e=>setFormCiclo(p=>({...p,vlrContaConcessionaria:e.target.value}))}/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Consumo Total Medido (m³) *</label>
                    <input type="number" step="0.001" className="form-control"
                      placeholder="0,000"
                      value={formCiclo.consumoTotalM3}
                      onChange={e=>setFormCiclo(p=>({...p,consumoTotalM3:e.target.value}))}/>
                    <small style={{fontSize:11,color:"var(--gray-400)"}}>
                      Conforme consta na conta da concessionária
                    </small>
                  </div>
                </div>
                {formCiclo.vlrContaConcessionaria && formCiclo.consumoTotalM3 && (
                  <div style={{marginTop:12,background:bgTp,borderRadius:8,padding:"8px 12px",fontSize:13}}>
                    <strong style={{color:corTp}}>Custo por m³: </strong>
                    <span style={{color:corTp,fontWeight:700}}>
                      {fmtM(Number(formCiclo.vlrContaConcessionaria)/Number(formCiclo.consumoTotalM3))} /m³
                    </span>
                  </div>
                )}

                <button className="btn btn-outline-primary w-100 mt-3" onClick={handleCarregarLeituras}
                  disabled={!idCondo || medidores.filter(m=>m.ativo).length===0}
                  style={{fontWeight:600}}>
                  <i className="bi bi-arrow-down-circle me-2"/>
                  {medidores.filter(m=>m.ativo).length === 0
                    ? "⚠️ Nenhum medidor ativo cadastrado"
                    : `Carregar ${medidores.filter(m=>m.ativo).length} Medidor(es)`}
                </button>
                {medidores.filter(m=>m.ativo).length === 0 && idCondo && (
                  <small style={{color:"var(--gray-400)",fontSize:11,display:"block",marginTop:4,textAlign:"center"}}>
                    Cadastre medidores na aba Medidores para habilitar.
                  </small>
                )}
              </div>

              {/* Painel direito: leituras por unidade */}
              <div style={{background:"var(--bg-surface)",borderRadius:12,padding:20,border:"1px solid var(--gray-200)"}}>
                <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)",marginBottom:14,
                  display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span><i className={`bi ${icone} me-2`} style={{color:corTp}}/>Leituras por Unidade</span>
                  <span style={{fontSize:12,color:"var(--gray-400)"}}>{leituras.length} medidor(es)</span>
                </div>

                {leituras.length === 0 ? (
                  <div style={{textAlign:"center",padding:"32px 0",color:"var(--gray-400)"}}>
                    <i className="bi bi-speedometer" style={{fontSize:32,display:"block",marginBottom:8}}/>
                    <p style={{fontSize:13}}>Clique em "Carregar Medidores" para iniciar</p>
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:340,overflowY:"auto"}}>
                    {leituras.map((l,i) => {
                      const lAtual  = parseFloat(l.leituraAtual);
                      const lAnt    = parseFloat(l.leituraAnterior);
                      const consumo = !isNaN(lAtual) && lAtual >= lAnt ? lAtual - lAnt : null;
                      const media   = parseFloat(l.mediaConsumo) || 0;
                      const pctAlert= (parseFloat(cfg?.alertaPercVariacao) || 30) / 100;
                      const variacao= consumo != null && media > 0 ? (consumo - media) / media : null;
                      const alerta  = variacao != null && Math.abs(variacao) >= pctAlert;
                      const zerou   = !isNaN(lAtual) && lAtual < lAnt;
                      return (
                        <div key={l.idMedidor} style={{
                          background: alerta ? "#FFF7ED" : zerou ? "#FEF2F2" : "var(--gray-50)",
                          borderRadius:8, padding:"10px 12px",
                          border:`1px solid ${alerta ? "#FED7AA" : zerou ? "#FECACA" : "var(--gray-100)"}`,
                        }}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,gap:6,flexWrap:"wrap"}}>
                            <span style={{fontWeight:600,fontSize:13,color:"var(--gray-700)"}}>
                              {l.bloco?`Bl ${l.bloco} — `:""}Un {l.nmUnidade}
                              {l.nrMedidor && <span style={{fontSize:10,color:"var(--gray-400)",marginLeft:6}}>#{l.nrMedidor}</span>}
                            </span>
                            <span style={{fontSize:11,color:"var(--gray-500)"}}>
                              Ant: {fmtN3(l.leituraAnterior)} m³
                              {media > 0 && <span style={{marginLeft:8,color:"var(--gray-400)"}}>· média: {fmtN3(media)}</span>}
                            </span>
                          </div>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <input type="number" step="0.001" className="form-control form-control-sm"
                              placeholder="Leitura atual (m³)"
                              value={l.leituraAtual}
                              onChange={e=>{
                                const nl=[...leituras];
                                nl[i]={...nl[i],leituraAtual:e.target.value};
                                setLeituras(nl);
                              }}/>
                            <FotoLeituraButton
                              color={corTp}
                              onAplicar={({ leitura, base64, mimeType, leituraOCR, confiancaOCR }) => {
                                const nl=[...leituras];
                                nl[i] = {
                                  ...nl[i],
                                  leituraAtual: String(leitura),
                                  fotoPendente: { base64, mimeType, leituraOCR, confiancaOCR },
                                };
                                setLeituras(nl);
                              }}/>
                            {l.fotoPendente && (
                              <span title="Foto anexada (será salva ao confirmar)" style={{color:"#16A34A"}}>
                                <i className="bi bi-check-circle-fill"/>
                              </span>
                            )}
                            {consumo != null && (
                              <span style={{fontSize:12,color:corTp,fontWeight:600,whiteSpace:"nowrap"}}>
                                {fmtN3(consumo)} m³
                                {tpConsumo==="Gas" && (
                                  <span style={{color:"#C2410C",marginLeft:4,fontSize:11}}>
                                    ≈ {fmtN3(consumo * parseFloat(cfg?.fatorConversao||1))} kg
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                          {/* Indicadores */}
                          {(alerta || zerou || variacao != null) && (
                            <div style={{marginTop:6,fontSize:11,display:"flex",gap:8,flexWrap:"wrap"}}>
                              {zerou && (
                                <span style={{color:"#991B1B",fontWeight:600}}>
                                  <i className="bi bi-exclamation-octagon me-1"/>
                                  Leitura menor que anterior — medidor foi trocado?
                                </span>
                              )}
                              {alerta && !zerou && (
                                <span style={{color:"#9A3412",fontWeight:600}}>
                                  <i className="bi bi-exclamation-triangle me-1"/>
                                  Variação {variacao > 0 ? "+" : ""}{(variacao*100).toFixed(0)}% vs média
                                </span>
                              )}
                              {!alerta && !zerou && variacao != null && (
                                <span style={{color:"var(--gray-400)"}}>
                                  Variação {variacao > 0 ? "+" : ""}{(variacao*100).toFixed(0)}% vs média
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Botão de import CSV */}
                {medidores.filter(m=>m.ativo).length > 0 && (
                  <div style={{marginTop:10}}>
                    <label className="btn btn-outline-secondary btn-sm w-100" style={{cursor:"pointer"}}>
                      <i className="bi bi-file-earmark-spreadsheet me-1"/>Importar leituras (CSV)
                      <input type="file" accept=".csv,text/csv" style={{display:"none"}}
                        onChange={e=>{ if (e.target.files?.[0]) handleImportCSV(e.target.files[0]); e.target.value=""; }}/>
                    </label>
                    <small style={{fontSize:10,color:"var(--gray-400)",display:"block",marginTop:2}}>
                      Formato: idMedidor;leitura  ou  nrMedidor;leitura  (separador ; ou ,)
                    </small>
                  </div>
                )}

                {leituras.length > 0 && (
                  <button className="btn btn-primary w-100 mt-3" onClick={handlePreview}
                    disabled={loadingPreview||leituras.some(l=>!l.leituraAtual)}>
                    {loadingPreview
                      ? <><span className="spinner-border spinner-border-sm me-2"/>Calculando...</>
                      : <><i className="bi bi-calculator me-2"/>Preview do Rateio</>}
                  </button>
                )}
              </div>

              {/* Preview do rateio */}
              {preview && (
                <div style={{gridColumn:"1/-1",background:"var(--bg-surface)",borderRadius:12,
                  padding:20,border:`2px solid ${corTp}`}}>
                  <div style={{fontWeight:700,fontSize:15,color:corTp,marginBottom:16,
                    display:"flex",alignItems:"center",gap:8}}>
                    <i className="bi bi-calculator"/>
                    Preview do Rateio — {mesLabel(formCiclo.competencia)}
                  </div>

                  {/* Resumo */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
                    {[
                      {l:"Valor da Conta",    v:fmtM(preview.vlrConta),         c:corTp},
                      {l:"Total Medido",      v:`${fmtN3(preview.totalMedido)}m³`, c:"var(--gray-700)"},
                      {l:"Diferença",         v:`${fmtN3(preview.diferenca)}m³`,  c:preview.diferenca>0?"#C2410C":"#166534"},
                      {l:"Custo/m³",          v:fmtM(preview.vlrPorM3),          c:corTp},
                    ].map(s=>(
                      <div key={s.l} style={{background:"var(--gray-50)",borderRadius:8,padding:"10px 14px",textAlign:"center"}}>
                        <div style={{fontSize:16,fontWeight:700,color:s.c}}>{s.v}</div>
                        <div style={{fontSize:11,color:"var(--gray-400)"}}>{s.l}</div>
                      </div>
                    ))}
                  </div>

                  {/* Diferença */}
                  {preview.diferenca > 0 && (
                    <div style={{background:"#FFF7ED",borderRadius:8,padding:"8px 14px",
                      marginBottom:12,fontSize:13,color:"#C2410C",display:"flex",gap:8}}>
                      <i className="bi bi-info-circle-fill"/>
                      <span>
                        Diferença de {fmtN3(preview.diferenca)}m³ ({fmtM(preview.vlrDiferenca)}) 
                        será {preview.tpDiferenca==="Absorver"
                          ? `absorvida como despesa (${preview.descDiferenca})`
                          : "distribuída entre as unidades"}.
                      </span>
                    </div>
                  )}

                  {/* Tabela por unidade */}
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead>
                        <tr style={{background:"var(--gray-50)"}}>
                          {(tpConsumo==="Gas"
                            ? ["Unidade","Medidor","Leit. Ant.","Leit. Atual","Consumo m³","Consumo kg","Valor","Alerta"]
                            : ["Unidade","Medidor","Leit. Ant.","Leit. Atual","Consumo","Valor","Alerta"]
                          ).map(h=>(
                            <th key={h} style={{padding:"8px 10px",textAlign:"left",
                              fontWeight:600,color:"var(--gray-600)",fontSize:12}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(preview.itens||[]).map((it,i)=>(
                          <tr key={i} style={{borderBottom:"1px solid var(--gray-100)",
                            background:it.alerta?"#FEF9C3":"#fff"}}>
                            <td style={{padding:"7px 10px",fontWeight:600}}>{it.nmUnidade}</td>
                            <td style={{padding:"7px 10px",color:"var(--gray-400)",fontSize:12}}>{it.nrMedidor||"—"}</td>
                            <td style={{padding:"7px 10px"}}>{fmtN3(it.leituraAnterior)}</td>
                            <td style={{padding:"7px 10px"}}>{fmtN3(it.leituraAtual)}</td>
                            <td style={{padding:"7px 10px",fontWeight:600,color:corTp}}>{fmtN3(it.consumo||it.consumoM3)}m³</td>
                            {tpConsumo==="Gas" && (
                              <td style={{padding:"7px 10px",fontWeight:600,color:"#C2410C"}}>{fmtN3(it.consumoKg||((it.consumo||it.consumoM3)*parseFloat(cfg?.fatorConversao||1)))}kg</td>
                            )}
                            <td style={{padding:"7px 10px",fontWeight:700,color:corTp}}>{fmtM(it.vlrTotal)}</td>
                            <td style={{padding:"7px 10px"}}>
                              {it.alerta && <span style={{background:"var(--bg-warning-soft)",color:"#854D0E",
                                fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:10}}>
                                ⚠️ Alto
                              </span>}
                            </td>
                          </tr>
                        ))}
                        <tr style={{background:bgTp,fontWeight:700}}>
                          <td colSpan={4} style={{padding:"8px 10px",color:corTp}}>TOTAL</td>
                          <td style={{padding:"8px 10px",color:corTp}}>{fmtN3(preview.totalMedido)}m³</td>
                          <td style={{padding:"8px 10px",color:corTp}}>{fmtM(preview.vlrConta)}</td>
                          <td/>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div style={{display:"flex",gap:10,marginTop:16}}>
                    <button className="btn btn-outline-secondary" onClick={()=>setPreview(null)}>
                      <i className="bi bi-pencil me-1"/>Corrigir Leituras
                    </button>
                    <button className="btn btn-success ms-auto" onClick={handleConfirmar}
                      disabled={confirmando}>
                      {confirmando
                        ? <><span className="spinner-border spinner-border-sm me-2"/>Lançando...</>
                        : <><i className="bi bi-check-circle me-2"/>Confirmar e Lançar</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ ABA: HISTÓRICO ══ */}
          {aba==="historico" && (
            <div style={{background:"var(--bg-surface)",borderRadius:12,border:"1px solid var(--gray-200)",overflow:"hidden"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid var(--gray-100)",
                fontWeight:700,fontSize:14,color:"var(--gray-800)"}}>
                Histórico de Ciclos — {tpConsumo==="Agua"?"Água":"Gás"}
              </div>
              {loading ? (
                <div style={{textAlign:"center",padding:40}}>
                  <span className="spinner-border text-primary"/>
                </div>
              ) : !ciclos.length ? (
                <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                  <i className="bi bi-clock-history" style={{fontSize:32,display:"block",marginBottom:8}}/>
                  <p>Nenhum ciclo lançado ainda</p>
                </div>
              ) : (
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{background:"var(--gray-50)"}}>
                      {["Competência","Data Leitura","Valor Conta","Total m³","Diferença","Custo/m³","Status",""].map(h=>(
                        <th key={h} style={{padding:"10px 14px",textAlign:"left",
                          fontWeight:600,color:"var(--gray-600)",fontSize:12}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ciclos.map((c,i)=>(
                      <React.Fragment key={c.id}>
                      <tr style={{borderBottom:"1px solid var(--gray-100)",
                        background:i%2===0?"#fff":"var(--gray-50)"}}>
                        <td style={{padding:"10px 14px",fontWeight:600}}>{mesLabel(c.competencia)}</td>
                        <td style={{padding:"10px 14px"}}>{fmtD(c.dtLeitura)}</td>
                        <td style={{padding:"10px 14px",fontWeight:700,color:corTp}}>{fmtM(c.vlrContaConcessionaria)}</td>
                        <td style={{padding:"10px 14px"}}>{fmtN3(c.consumoTotalM3)}m³</td>
                        <td style={{padding:"10px 14px",color:c.diferencaM3>0?"#C2410C":"#166534"}}>
                          {fmtN3(c.diferencaM3)}m³
                        </td>
                        <td style={{padding:"10px 14px"}}>{fmtM(c.vlrPorM3)}/m³</td>
                        <td style={{padding:"10px 14px"}}>
                          <span style={{
                            background:c.status==="Incluido"?"#DCFCE7":c.status==="Confirmado"?"#EFF6FF":"#FEF9C3",
                            color:c.status==="Incluido"?"#166534":c.status==="Confirmado"?"#1D4ED8":"#854D0E",
                            fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:10}}>
                            {c.status}
                          </span>
                        </td>
                        <td style={{padding:"10px 14px"}}>
                          <button className="btn btn-outline-secondary btn-sm" style={{fontSize:11}}
                            onClick={async ()=>{
                              const newId = c.id===cicloAtivo?null:c.id;
                              setCicloAtivo(newId);
                              if(newId && !cicloLeituras[newId]) {
                                try {
                                  const r2 = await consumoService.listarLeituras({idCondominio:idCondo, tpConsumo, competencia:c.competencia});
                                  setCicloLeituras(p=>({...p,[newId]:r2.data||[]}));
                                } catch{}
                              }
                            }}>
                            <i className={`bi bi-chevron-${cicloAtivo===c.id?"up":"down"}`}/>
                          </button>
                        </td>
                      </tr>
                      {cicloAtivo===c.id && (cicloLeituras[c.id]||[]).map(lm=>(
                        <tr key={lm.id} style={{background:"#F0F9FF",borderBottom:"1px solid #BAE6FD"}}>
                          <td style={{padding:"6px 14px 6px 28px",fontSize:12,color:"var(--gray-600)"}}>
                            <i className="bi bi-arrow-return-right me-1"/>
                            {lm.nmUnidade}{lm.bloco?` / Bl.${lm.bloco}`:""}
                          </td>
                          <td style={{padding:"6px 14px",fontSize:12}}>{lm.nrMedidor||"—"}</td>
                          <td style={{padding:"6px 14px",fontSize:12}}>{fmtN3(lm.leituraAnterior)}</td>
                          <td style={{padding:"6px 14px",fontSize:12,fontWeight:600,color:corTp}}>{fmtN3(lm.leituraAtual)}</td>
                          <td style={{padding:"6px 14px",fontSize:12}}>{fmtN3(lm.consumoM3)} m³</td>
                          <td style={{padding:"6px 14px",fontSize:12}}></td>
                          <td style={{padding:"6px 14px",fontSize:12,fontWeight:700,color:corTp}}>{fmtM(lm.vlrCobrar)}</td>
                          <td></td>
                        </tr>
                      ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ══ ABA: MEDIDORES ══ */}
          {aba==="medidores" && (
            <div style={{background:"var(--bg-surface)",borderRadius:12,border:"1px solid var(--gray-200)",overflow:"hidden"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid var(--gray-100)",
                display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontWeight:700,fontSize:14,color:"var(--gray-800)"}}>
                  Medidores de {tpConsumo==="Agua"?"Água":"Gás"} ({medidores.length})
                </span>
                <button className="btn btn-primary btn-sm" onClick={()=>{
                  setEditMed(null);
                  setFormMed({idUnidade:"",nrMedidor:"",leituraInicial:"0",dtInstalacao:"",observacao:""});
                  setShowMedModal(true);
                }}>
                  <i className="bi bi-plus me-1"/>Adicionar Medidor
                </button>
              </div>
              {medidores.length === 0 ? (
                <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                  <i className="bi bi-speedometer" style={{fontSize:32,display:"block",marginBottom:8}}/>
                  <p>Nenhum medidor cadastrado</p>
                </div>
              ) : (
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{background:"var(--gray-50)"}}>
                      {["Unidade","Nº Medidor","Leitura Inicial","Instalação","Status",""].map(h=>(
                        <th key={h} style={{padding:"10px 14px",textAlign:"left",
                          fontWeight:600,color:"var(--gray-600)",fontSize:12}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {medidores.map((m,i)=>(
                      <tr key={m.id} style={{borderBottom:"1px solid var(--gray-100)",
                        background:i%2===0?"#fff":"var(--gray-50)"}}>
                        <td style={{padding:"10px 14px",fontWeight:600}}>{m.nmUnidade||"—"}</td>
                        <td style={{padding:"10px 14px"}}>{m.nrMedidor||"—"}</td>
                        <td style={{padding:"10px 14px"}}>{fmtN3(m.leituraInicial)} m³</td>
                        <td style={{padding:"10px 14px"}}>{fmtD(m.dtInstalacao)}</td>
                        <td style={{padding:"10px 14px"}}>
                          <span style={{background:m.ativo?"#DCFCE7":"#FEE2E2",
                            color:m.ativo?"#166534":"#991B1B",fontSize:11,fontWeight:700,
                            padding:"2px 8px",borderRadius:10}}>
                            {m.ativo?"Ativo":"Inativo"}
                          </span>
                        </td>
                        <td style={{padding:"10px 14px"}}>
                          <button className="btn btn-outline-secondary btn-sm btn-icon"
                            onClick={()=>{setEditMed(m);setFormMed({...m});setShowMedModal(true);}}>
                            <i className="bi bi-pencil"/>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ══ ABA: CONFIGURAÇÃO ══ */}
          {aba==="config" && (
            <div style={{background:"var(--bg-surface)",borderRadius:12,padding:24,border:"1px solid var(--gray-200)",
              maxWidth:700}}>
              <div style={{fontWeight:700,fontSize:15,color:"var(--gray-800)",marginBottom:20,
                display:"flex",alignItems:"center",gap:8,justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <i className={`bi ${icone}`} style={{color:corTp}}/>
                  Configuração de {tpConsumo==="Agua"?"Água":"Gás"}
                </div>
                <span style={{
                  fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:20,
                  background:cfg?"#DCFCE7":"#FEF9C3",
                  color:cfg?"#166534":"#854D0E"
                }}>
                  {cfg ? "✅ Configuração salva" : "⚠️ Não configurado"}
                </span>
              </div>
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">Conta Contábil — Receita</label>
                  <select className="form-select" value={formCfg.idCtReceita}
                    onChange={e=>setFormCfg(p=>({...p,idCtReceita:e.target.value}))}>
                    <option value="">Selecione</option>
                    {contasContabeis.map(c=><option key={c.id} value={c.id}>{c.classificacaoContabil} — {c.descConta}</option>)}
                  </select>
                  <small style={{fontSize:11,color:"var(--gray-400)"}}>ex: 1.1.03 Rateio de {tpConsumo==="Agua"?"Água":"Gás"}</small>
                </div>
                <div className="col-6">
                  <label className="form-label">Conta Contábil — Despesa</label>
                  <select className="form-select" value={formCfg.idCtDespesa}
                    onChange={e=>setFormCfg(p=>({...p,idCtDespesa:e.target.value}))}>
                    <option value="">Selecione</option>
                    {contasContabeis.map(c=><option key={c.id} value={c.id}>{c.classificacaoContabil} — {c.descConta}</option>)}
                  </select>
                  <small style={{fontSize:11,color:"var(--gray-400)"}}>ex: 2.4.01 {tpConsumo==="Agua"?"Água e Esgoto":"Gás (GLP/GN)"}</small>
                </div>
                <div className="col-6">
                  <label className="form-label">Fornecedor (Concessionária)</label>
                  <select className="form-select" value={formCfg.idFornecedor}
                    onChange={e=>setFormCfg(p=>({...p,idFornecedor:e.target.value}))}>
                    <option value="">Selecione</option>
                    {fornecedores.map(f=><option key={f.id} value={f.id}>{f.nmReduzido||f.nmRazaoSocial||f.nmFornecedor}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Conta Bancária</label>
                  <select className="form-select" value={formCfg.idContaBancaria}
                    onChange={e=>setFormCfg(p=>({...p,idContaBancaria:e.target.value}))}>
                    <option value="">Selecione</option>
                    {contasBancarias.map(c=><option key={c.id} value={c.id}>{c.nmConta}</option>)}
                  </select>
                </div>
                <div className="col-4">
                  <label className="form-label">Franquia (m³)</label>
                  <input type="number" step="0.001" className="form-control"
                    value={formCfg.franquiaM3}
                    onChange={e=>setFormCfg(p=>({...p,franquiaM3:e.target.value}))}/>
                  <small style={{fontSize:11,color:"var(--gray-400)"}}>m³ inclusos na cota (0 = sem franquia)</small>
                </div>
                <div className="col-4">
                  <label className="form-label">Alerta de Variação (%)</label>
                  <input type="number" className="form-control"
                    value={formCfg.alertaPercVariacao}
                    onChange={e=>setFormCfg(p=>({...p,alertaPercVariacao:e.target.value}))}/>
                  <small style={{fontSize:11,color:"var(--gray-400)"}}>Alerta se consumo variar mais que X%</small>
                </div>
                {/* Campos específicos do Gás */}
                {tpConsumo==="Gas" && (<>
                  <div className="col-4">
                    <label className="form-label">Unidade de Medida da Tarifa</label>
                    <select className="form-select" value={formCfg.unidadeMedida}
                      onChange={e=>setFormCfg(p=>({...p,unidadeMedida:e.target.value}))}>
                      <option value="m3">m³ (metro cúbico)</option>
                      <option value="kg">kg (quilograma)</option>
                    </select>
                    <small style={{fontSize:11,color:"var(--gray-400)"}}>
                      GLP geralmente cobra em kg, GN em m³
                    </small>
                  </div>
                  {formCfg.unidadeMedida==="kg" && (
                    <div className="col-4">
                      <label className="form-label">Fator de Conversão (m³ → kg)</label>
                      <input type="number" step="0.000001" className="form-control"
                        value={formCfg.fatorConversao}
                        onChange={e=>setFormCfg(p=>({...p,fatorConversao:e.target.value}))}/>
                      <small style={{fontSize:11,color:"var(--gray-400)"}}>
                        Consta na conta da distribuidora. GLP≈0,52 · GN varia por região
                      </small>
                    </div>
                  )}
                </>)}
                <div className="col-12">
                  <label className="form-label">Tratamento da Diferença (Perdas/Uso Comum)</label>
                  <div style={{display:"flex",gap:12}}>
                    {[
                      {v:"Absorver",   label:"Absorver como despesa do condomínio"},
                      {v:"Distribuir", label:"Distribuir entre as unidades"},
                    ].map(op=>(
                      <label key={op.v} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",
                        background:formCfg.tpDiferenca===op.v?bgTp:"var(--gray-50)",
                        borderRadius:8,padding:"10px 14px",border:`1px solid ${formCfg.tpDiferenca===op.v?corTp:"var(--gray-200)"}`,
                        flex:1}}>
                        <input type="radio" name="tpDiferenca" value={op.v}
                          checked={formCfg.tpDiferenca===op.v}
                          onChange={e=>setFormCfg(p=>({...p,tpDiferenca:e.target.value}))}/>
                        <span style={{fontSize:13,color:formCfg.tpDiferenca===op.v?corTp:"var(--gray-700)"}}>{op.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {formCfg.tpDiferenca==="Absorver" && (
                  <div className="col-12">
                    <label className="form-label">Conta Contábil — Diferença/Perdas</label>
                    <select className="form-select" value={formCfg.idCtDiferenca}
                      onChange={e=>setFormCfg(p=>({...p,idCtDiferenca:e.target.value}))}>
                      <option value="">Mesma conta de despesa</option>
                      {contasContabeis.map(c=><option key={c.id} value={c.id}>{c.classificacaoContabil} — {c.descConta}</option>)}
                    </select>
                  </div>
                )}
                {formCfg.tpDiferenca==="Distribuir" && (
                  <div className="col-12">
                    <label className="form-label">Descrição no boleto</label>
                    <input type="text" className="form-control"
                      value={formCfg.descDiferenca}
                      onChange={e=>setFormCfg(p=>({...p,descDiferenca:e.target.value}))}/>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <button className="btn btn-primary" onClick={handleSalvarConfig} disabled={savingCfg}>
                  {savingCfg
                    ? <><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>
                    : <><i className="bi bi-check-circle me-2"/>Salvar Configuração</>}
                </button>
              </div>
            </div>
          )}

          {/* ══ ABA: RELATÓRIO ══ */}
          {aba==="relatorio" && (
            <div>
              <div style={{display:"flex",gap:10,marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <label style={{fontSize:13,fontWeight:600,marginBottom:0,whiteSpace:"nowrap"}}>Competência:</label>
                  <input type="month" className="form-control" style={{width:160,fontSize:13}}
                    value={relCompetencia} onChange={e=>setRelCompetencia(e.target.value)}/>
                </div>
                <button className="btn btn-primary" onClick={handleCarregarRelatorio} disabled={loadingRel}>
                  {loadingRel ? <><span className="spinner-border spinner-border-sm me-2"/>Gerando...</>
                    : <><i className="bi bi-bar-chart-line me-2"/>Gerar Relatório de Consumo</>}
                </button>
              </div>
              {relatorio && (
                <div style={{background:"var(--bg-surface)",borderRadius:12,padding:20,border:"1px solid var(--gray-200)"}}>
                  <div style={{fontWeight:700,fontSize:15,color:"var(--gray-800)",marginBottom:16}}>
                    Histórico de Consumo — Últimos 12 meses
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead>
                        <tr style={{background:"var(--gray-50)"}}>
                          <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:"var(--gray-600)",fontSize:12}}>Competência</th>
                          <th style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:"var(--gray-600)",fontSize:12}}>Total m³</th>
                          <th style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:"var(--gray-600)",fontSize:12}}>Valor Conta</th>
                          <th style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:"var(--gray-600)",fontSize:12}}>Custo/m³</th>
                          <th style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:"var(--gray-600)",fontSize:12}}>Alertas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(relatorio.ciclos||[]).map((c,i)=>(
                          <tr key={c.id} style={{borderBottom:"1px solid var(--gray-100)",background:i%2===0?"#fff":"var(--gray-50)"}}>
                            <td style={{padding:"9px 12px",fontWeight:600}}>{mesLabel(c.competencia)}</td>
                            <td style={{padding:"9px 12px",textAlign:"right",color:corTp,fontWeight:700}}>{fmtN3(c.consumoTotalM3)}m³</td>
                            <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700}}>{fmtM(c.vlrContaConcessionaria)}</td>
                            <td style={{padding:"9px 12px",textAlign:"right"}}>{fmtM(c.vlrPorM3)}/m³</td>
                            <td style={{padding:"9px 12px",textAlign:"right"}}>
                              {c.qtdAlertas>0 && <span style={{background:"var(--bg-warning-soft)",color:"#854D0E",
                                fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:10}}>
                                ⚠️ {c.qtdAlertas}
                              </span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Modal Medidor ── */}
      {showMedModal && (
        <div className="modal-overlay" onClick={()=>setShowMedModal(false)}>
          <div className="modal-box" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className={`bi ${icone} me-2`} style={{color:corTp}}/>
                {editMed?"Editar":"Novo"} Medidor de {tpConsumo==="Agua"?"Água":"Gás"}
              </h5>
              <button className="btn-close" onClick={()=>setShowMedModal(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Unidade *</label>
                  <select className="form-select" value={formMed.idUnidade}
                    onChange={e=>setFormMed(p=>({...p,idUnidade:e.target.value}))}>
                    <option value="">Selecione</option>
                    {unidades.map(u=><option key={u.id} value={u.id}>{u.unidade}{u.bloco?`/Bl.${u.bloco}`:""}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Nº do Medidor</label>
                  <input className="form-control" value={formMed.nrMedidor}
                    onChange={e=>setFormMed(p=>({...p,nrMedidor:e.target.value}))}
                    placeholder="Número de série"/>
                </div>
                <div className="col-6">
                  <label className="form-label">Leitura Inicial (m³)</label>
                  <input type="number" step="0.001" className="form-control"
                    value={formMed.leituraInicial}
                    onChange={e=>setFormMed(p=>({...p,leituraInicial:e.target.value}))}/>
                </div>
                <div className="col-6">
                  <label className="form-label">Data de Instalação</label>
                  <input type="date" className="form-control" value={formMed.dtInstalacao}
                    onChange={e=>setFormMed(p=>({...p,dtInstalacao:e.target.value}))}/>
                </div>
                <div className="col-6">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={formMed.ativo}
                    onChange={e=>setFormMed(p=>({...p,ativo:Number(e.target.value)}))}>
                    <option value={1}>Ativo</option>
                    <option value={0}>Inativo</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowMedModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSalvarMedidor} disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm"/> : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Banner global de feedback ── */}
      {banner && (
        <div style={{position:"fixed",top:16,right:16,zIndex:9999,maxWidth:480}}>
          <Banner tipo={banner.tipo} onClose={()=>setBanner(null)}>{banner.msg}</Banner>
        </div>
      )}

      {/* ── Modal de confirmação ── */}
      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          perigo={confirmacao.perigo}
          textoOk={confirmacao.textoOk}
          onConfirm={confirmacao.onConfirm}
          onCancel={confirmacao.onCancel}
        />
      )}
    </div>
  );
}
